import type React from "react";
import { Box, Paper, Typography, alpha } from "@mui/material";

interface DetailSectionCardProps {
  title: string;
  icon: React.ReactNode;
  iconColor: string;
  children?: React.ReactNode;
  headerMeta?: React.ReactNode;
  headerActions?: React.ReactNode;
  sx?: object;
  "data-testid"?: string;
}

const iconBoxSx = (iconColor: string, compact: boolean) => ({
  width: compact ? 28 : 36,
  height: compact ? 28 : 36,
  borderRadius: compact ? 1.25 : 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: alpha(iconColor, 0.12),
  color: iconColor,
  flexShrink: 0,
});

const DetailSectionCard: React.FC<DetailSectionCardProps> = ({
  title,
  icon,
  iconColor,
  children,
  headerMeta,
  headerActions,
  sx,
  "data-testid": testId,
}) => {
  const hasCompactHeader = headerMeta != null || headerActions != null;

  if (hasCompactHeader) {
    return (
      <Paper
        elevation={0}
        data-testid={testId}
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          ...sx,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            rowGap: 0.75,
            flexWrap: "wrap",
          }}
        >
          <Box sx={iconBoxSx(iconColor, true)}>{icon}</Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
            {title}
          </Typography>
          {headerMeta ? (
            <>
              <Box component="span" sx={{ color: "text.disabled" }}>
                ·
              </Box>
              <Box sx={{ minWidth: 0 }}>{headerMeta}</Box>
            </>
          ) : null}
          {headerActions ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                ml: "auto",
              }}
            >
              {headerActions}
            </Box>
          ) : null}
        </Box>
        {children}
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      data-testid={testId}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: children ? 2 : 0 }}>
        <Box sx={iconBoxSx(iconColor, false)}>{icon}</Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  );
};

export default DetailSectionCard;
